import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import type { ColumnDef } from '@tanstack/react-table';
import { graphql } from '@/gql';
import { Badge, type BadgeTone } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { DataGrid } from '@/components/ui/DataGrid';
import { Modal } from '@/components/ui/Modal';
import { formatDate, formatDateTime } from '@/lib/format';
import { hasPermission } from '@/lib/permissions';
import { useUrlState } from '@/hooks/useUrlState';
import { recognitionDaySessionsUrlSchema } from './recognitionDaySessionsUrlState';
import { useSession } from '@/features/session/useSession';
import {
  ActiveRecognitionDayDoc,
  useRecognitionDayOpen,
} from '@/features/session/useRecognitionDayOpen';

const RecognitionDaySessionsDoc = graphql(/* GraphQL */ `
  query RecognitionDaySessions($page: Int, $pageSize: Int) {
    recognitionDaySessions(page: $page, pageSize: $pageSize) {
      nodes {
        id
        recognitionDate
        isOpen
        openedAt
        closedAt
        closeReason
      }
      pageInfo {
        page
        pageSize
        totalRows
        totalPages
      }
    }
  }
`);

const EndRecognitionDayDoc = graphql(/* GraphQL */ `
  mutation EndRecognitionDay {
    endRecognitionDay {
      id
      recognitionDate
      isOpen
      openedAt
      closedAt
      closeReason
    }
  }
`);

interface SessionRow {
  id: string;
  recognitionDate: string;
  isOpen: boolean;
  openedAt: string;
  closedAt?: string | null;
  closeReason?: string | null;
}

function closeReasonLabel(reason: string | null | undefined): string {
  if (!reason) return '—';
  if (reason === 'manual') return 'Manual';
  if (reason === 'cutoff') return 'Cutoff';
  return reason;
}

/**
 * Paginated recognition-day sessions for the current scope. End day is offered
 * on the open row when the actor has pos:operate (mutation only — no wizard).
 */
export function RecognitionDaySessionsListPage() {
  const { me } = useSession();
  const canEndDay = hasPermission(me?.permissions, 'pos:operate');
  const isOpen = useRecognitionDayOpen();

  const { state: url, patch, setFilters } = useUrlState(recognitionDaySessionsUrlSchema);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [endError, setEndError] = useState<string | null>(null);

  const { data, previousData, loading, refetch } = useQuery(
    RecognitionDaySessionsDoc,
    {
      variables: { page: url.page, pageSize: url.size },
    },
  );
  const viewData = data ?? previousData;
  const connection = viewData?.recognitionDaySessions;
  const pageInfo = connection?.pageInfo;

  const [endDay, { loading: ending }] = useMutation(EndRecognitionDayDoc, {
    update(cache, result) {
      const session = result.data?.endRecognitionDay;
      cache.writeQuery({
        query: ActiveRecognitionDayDoc,
        data: {
          activeRecognitionDay:
            session && session.isOpen ? session : null,
        },
      });
    },
  });

  // Soft-refresh when open/close changes (POS or this page ending the day).
  useEffect(() => {
    if (isOpen === null) return;
    void refetch();
  }, [isOpen, refetch]);

  const rows: SessionRow[] = useMemo(
    () =>
      (connection?.nodes ?? []).map((n) => ({
        id: n.id,
        recognitionDate: n.recognitionDate,
        isOpen: n.isOpen,
        openedAt: n.openedAt,
        closedAt: n.closedAt,
        closeReason: n.closeReason,
      })),
    [connection],
  );

  const columns = useMemo<ColumnDef<SessionRow, unknown>[]>(
    () => [
      {
        id: 'recognitionDate',
        header: 'Recognition date',
        accessorKey: 'recognitionDate',
        cell: ({ row }) => formatDate(row.original.recognitionDate),
      },
      {
        id: 'status',
        header: 'Status',
        accessorKey: 'isOpen',
        cell: ({ row }) => {
          const open = row.original.isOpen;
          const tone: BadgeTone = open ? 'success' : 'neutral';
          return <Badge tone={tone}>{open ? 'Open' : 'Closed'}</Badge>;
        },
      },
      {
        id: 'openedAt',
        header: 'Opened',
        accessorKey: 'openedAt',
        cell: ({ row }) => formatDateTime(row.original.openedAt),
      },
      {
        id: 'closedAt',
        header: 'Closed',
        accessorKey: 'closedAt',
        cell: ({ row }) =>
          row.original.closedAt
            ? formatDateTime(row.original.closedAt)
            : '—',
      },
      {
        id: 'closeReason',
        header: 'Close reason',
        accessorKey: 'closeReason',
        cell: ({ row }) => closeReasonLabel(row.original.closeReason),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => {
          if (!row.original.isOpen || !canEndDay) return null;
          return (
            <Button
              type="button"
              size="sm"
              variant="danger"
              onClick={() => {
                setEndError(null);
                setConfirmEnd(true);
              }}
            >
              End day
            </Button>
          );
        },
      },
    ],
    [canEndDay],
  );

  const handleConfirmEnd = async () => {
    setEndError(null);
    try {
      await endDay();
      setConfirmEnd(false);
      await refetch();
    } catch (err) {
      setEndError(
        err instanceof Error
          ? err.message
          : 'Could not end recognition day.',
      );
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
          Recognition day sessions
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Open and closed trading-day sessions for the current scope. Ending
          the day hibernates POS devices.
        </p>
      </div>

      <DataGrid
        columns={columns}
        data={rows}
        rowId={(row) => row.id}
        loading={loading && !viewData}
        emptyState="No recognition day sessions yet."
        pagination={{
          page: pageInfo?.page ?? url.page,
          pageSize: pageInfo?.pageSize ?? url.size,
          totalRows: pageInfo?.totalRows ?? 0,
          totalPages: pageInfo?.totalPages ?? 1,
          onPageChange: (next) => patch({ page: next }),
          onPageSizeChange: (size) => setFilters({ size }),
        }}
      />

      <Modal
        open={confirmEnd}
        onClose={() => {
          if (ending) return;
          setConfirmEnd(false);
          setEndError(null);
        }}
        title="End recognition day?"
        description="POS devices will hibernate. This does not run the end-of-day wizard (open tabs / reports)."
        footer={
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={ending}
              onClick={() => {
                setConfirmEnd(false);
                setEndError(null);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="danger"
              loading={ending}
              onClick={() => void handleConfirmEnd()}
            >
              End day
            </Button>
          </div>
        }
      >
        {endError ? (
          <p className="text-sm text-red-600 dark:text-red-400">{endError}</p>
        ) : (
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Close the open recognition-day session for this scope now?
          </p>
        )}
      </Modal>
    </div>
  );
}
